import { useState, useCallback } from "react";
import { openDoc, downloadDoc } from "./api";
import { useToast } from "./toast";
import { useT } from "./i18n";

// Shared, authenticated document printer/exporter with loading + success/failure feedback.
// Reuses the SINGLE backend PDF engine — no duplicate implementations.
// Usage:
//   const { printDoc, exportDoc, printing } = usePrintDoc();
//   <Button loading={printing === "fresh-print"} onClick={() => printDoc(path, { key: "fresh-print", label })} />
//   <Button loading={printing === "fresh-export"} onClick={() => exportDoc(path, { key: "fresh-export", label })} />
export function usePrintDoc() {
  const toast = useToast();
  const { t, lang } = useT();
  const [printing, setPrinting] = useState(null);

  const run = useCallback(async (fn, verb, path, { key, label, params = {} } = {}) => {
    const k = key || path;
    setPrinting(k);
    toast.info(`${label || t("Document")} — ${t("Generating document…")}`);
    try {
      await fn(path, { lang, ...params });
      toast.success(`${label || t("Document")} ${verb}`);
    } catch (e) {
      toast.error(t("Failed to open document"));
    } finally {
      setPrinting(null);
    }
  }, [lang, t, toast]);

  const printDoc = useCallback((path, opts) => run(openDoc, t("ready"), path, opts), [run, t]);
  const exportDoc = useCallback((path, opts) => run(downloadDoc, t("downloaded"), path, opts), [run, t]);

  return { printDoc, exportDoc, printing };
}
